import React from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ApplicationLog, LogLevel, LOG_LEVEL_COLORS } from '../../../../types/ApplicationLog';

interface LogDetailsProps {
  log: ApplicationLog;
  onClose: () => void;
}

const LogDetails: React.FC<LogDetailsProps> = ({ log, onClose }) => {
  const { t } = useTranslation();

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleString('nb-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${dateStr}.${ms}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('warehouse.modules.logs.logDetails')}
            </h2>
            <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${LOG_LEVEL_COLORS[log.level as LogLevel] || 'text-gray-600'} bg-opacity-10`}>
              {log.level}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('common.fields.id')}
                </label>
                <p className="text-sm text-gray-900">{log.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.timestamp')}
                </label>
                <p className="text-sm text-gray-900">{formatTimestamp(log.timeStamp)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.level')}
                </label>
                <p className={`text-sm font-medium ${LOG_LEVEL_COLORS[log.level as LogLevel] || 'text-gray-600'}`}>
                  {log.level}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.machine')}
                </label>
                <p className="text-sm text-gray-900">{log.machineName || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.version', {}, 'Versjon')}
                </label>
                <p className="text-sm text-gray-900 font-mono">{log.version || '-'}</p>
              </div>
            </div>

            {/* Message */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-500">
                  {t('warehouse.modules.logs.message')}
                </label>
                <button
                  onClick={() => copyToClipboard(log.message)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {t('warehouse.modules.logs.copy')}
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">
                  {log.message}
                </pre>
              </div>
            </div>

            {/* Request Information */}
            {(log.requestPath || log.requestMethod) && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.request')}
                </label>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  {log.requestMethod && (
                    <p className="text-sm">
                      <span className="font-medium">Method:</span> {log.requestMethod}
                    </p>
                  )}
                  {log.requestPath && (
                    <p className="text-sm">
                      <span className="font-medium">Path:</span> {log.requestPath}
                    </p>
                  )}
                  {log.clientIP && (
                    <p className="text-sm">
                      <span className="font-medium">Client IP:</span> {log.clientIP}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Operator Information */}
            {(log.operatorId || log.operatorName) && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.operator')}
                </label>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  {log.operatorId && (
                    <p className="text-sm">
                      <span className="font-medium">ID:</span> {log.operatorId}
                    </p>
                  )}
                  {log.operatorName && (
                    <p className="text-sm">
                      <span className="font-medium">Name:</span> {log.operatorName}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Tenant Information */}
            {(log.companyNo || log.customerNo) && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.tenant')}
                </label>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  {log.companyNo && (
                    <p className="text-sm">
                      <span className="font-medium">Company:</span> {log.companyNo}
                    </p>
                  )}
                  {log.customerNo && (
                    <p className="text-sm">
                      <span className="font-medium">Customer:</span> {log.customerNo}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Correlation ID */}
            {log.correlationId && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {t('warehouse.modules.logs.correlationId')}
                </label>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-gray-900 font-mono">{log.correlationId}</p>
                  <button
                    onClick={() => copyToClipboard(log.correlationId!)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('warehouse.modules.logs.copy')}
                  </button>
                </div>
              </div>
            )}

            {/* Exception */}
            {log.exception && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-red-600">
                    {t('warehouse.modules.logs.exception')}
                  </label>
                  <button
                    onClick={() => copyToClipboard(log.exception!)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('warehouse.modules.logs.copy')}
                  </button>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-64 overflow-auto">
                  <pre className="text-xs text-red-800 whitespace-pre-wrap break-words font-mono">
                    {log.exception}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogDetails;